import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, CreditCard, Search } from 'lucide-react';

import { MasterLookupBar }    from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }        from '@/components/master/MasterFlash';
import { MasterAuditTab }     from '@/components/master/MasterAuditTab';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Input }               from '@/components/ui/Input';
import { Badge }               from '@/components/ui/Badge';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { Combobox } from '@/components/ui/Combobox';
import { Textarea } from '@/components/ui/Textarea';
import { Card }                from '@/components/ui/Card';

import { usePermission }           from '@/hooks/usePermission';
import { useFlashMessage }    from '@/hooks/useFlashMessage';
import { useMasterScreenState }    from '@/hooks/useMasterScreenState';
import { Permissions }             from '@/constants/permissions';
import { AppRoutes }               from '@/constants/routes';
import { paymentMethodConfigApi }  from '@/api/paymentMethodConfig.api';
import type { PaymentMethodConfigDto, PaymentMethodConfigListDto, MethodCategory } from '@/types/paymentMethodConfig.types';
import type { RootState } from '@/state/store';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const METHOD_CATEGORIES: MethodCategory[] = ['CASH', 'CHEQUE', 'CARD', 'BANK_TRANSFER', 'WALLET', 'QR_CODE', 'OTHER'];

const schema = z.object({
  methodCode:            z.string().min(1, 'Method code is required').max(20),
  methodName:            z.string().min(1, 'Method name is required').max(100),
  methodCategory:        z.enum(['CASH', 'CHEQUE', 'CARD', 'BANK_TRANSFER', 'WALLET', 'QR_CODE', 'OTHER']),
  displayOrder:          z.coerce.number().int().min(0),
  requiresBankAccount:   z.boolean(),
  requiresChequeNumber:  z.boolean(),
  requiresChequeDate:    z.boolean(),
  requiresReference:     z.boolean(),
  allowedForAr:          z.boolean(),
  allowedForAp:          z.boolean(),
  allowedForPettyCash:   z.boolean(),
  isActive:              z.boolean(),
  remarks:               z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  methodCode: '', methodName: '', methodCategory: 'CASH', displayOrder: 0,
  requiresBankAccount: false, requiresChequeNumber: false, requiresChequeDate: false,
  requiresReference: false,
  allowedForAr: true, allowedForAp: true, allowedForPettyCash: false,
  isActive: true, remarks: null,
};

const TABS = ['General', 'Settings', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<PaymentMethodConfigListDto>[] = [
  { key: 'methodCode',     label: 'Code',     className: 'w-24 font-mono' },
  { key: 'methodName',     label: 'Name' },
  { key: 'methodCategory', label: 'Category', className: 'w-28' },
  { key: 'allowedForAr',   label: 'AR',       className: 'w-16',
    render: r => r.allowedForAr ? <Badge variant="success">Yes</Badge> : <Badge variant="default">No</Badge> },
  { key: 'allowedForAp',   label: 'AP',       className: 'w-16',
    render: r => r.allowedForAp ? <Badge variant="success">Yes</Badge> : <Badge variant="default">No</Badge> },
  { key: 'displayOrder',   label: 'Order',    className: 'w-16' },
  { key: 'isActive',       label: 'Status',   className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: PaymentMethodConfigDto): FormValues {
  return {
    methodCode: dto.methodCode,
    methodName: dto.methodName,
    methodCategory: dto.methodCategory,
    displayOrder: dto.displayOrder,
    requiresBankAccount: dto.requiresBankAccount,
    requiresChequeNumber: dto.requiresChequeNumber,
    requiresChequeDate:   dto.requiresChequeDate,
    requiresReference: dto.requiresReference,
    allowedForAr: dto.allowedForAr,
    allowedForAp: dto.allowedForAp,
    allowedForPettyCash: dto.allowedForPettyCash,
    isActive: dto.isActive,
    remarks: dto.remarks ?? null,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const PaymentMethodConfigPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const companyId = useSelector((s: RootState) => s.session.session?.companyId) ?? '';

  const canCreate = hasPermission(Permissions.PMT_METHOD_CREATE);
  const canEdit   = hasPermission(Permissions.PMT_METHOD_EDIT);
  const canDelete = hasPermission(Permissions.PMT_METHOD_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();

  const [record,       setRecord]       = useState<PaymentMethodConfigDto | null>(null);
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    register, handleSubmit, control, reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const resetToInitial = useCallback(() => {
    setRecord(null); reset(EMPTY_FORM);
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: PaymentMethodConfigDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleEdit      = () => screen.setMode('edit');
  const handleRefresh   = () => resetToInitial();
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await paymentMethodConfigApi.create({
          companyId,
          methodCode: d.methodCode.trim().toUpperCase(),
          methodName: d.methodName.trim(),
          methodCategory: d.methodCategory,
          displayOrder: d.displayOrder,
        });
        flash('success', 'Payment method created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await paymentMethodConfigApi.update(record.id, {
          methodName: d.methodName.trim(),
          methodCategory: d.methodCategory,
          isActive: d.isActive,
          requiresBankAccount: d.requiresBankAccount,
          requiresChequeNumber: d.requiresChequeNumber,
          requiresChequeDate:   d.requiresChequeDate,
          requiresReference: d.requiresReference,
          allowedForAr: d.allowedForAr,
          allowedForAp: d.allowedForAp,
          allowedForPettyCash: d.allowedForPettyCash,
          displayOrder: d.displayOrder,
          remarks: n(d.remarks),
        });
        flash('success', 'Payment method updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await paymentMethodConfigApi.remove(record.id);
      flash('success', `Payment method '${record.methodCode}' deleted.`); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handleExit = () => navigate(AppRoutes.DASHBOARD);

  const handleNewRecord = () => {
    if (!canCreate) { flash('error', 'No permission to create.'); return; }
    reset(EMPTY_FORM); setRecord(null);
    screen.setMode('new'); setActiveTab('General');
  };

  const fetchItems = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await paymentMethodConfigApi.getList({ companyId, search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, [companyId]);

  const handleSearchSelect = (item: PaymentMethodConfigListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    paymentMethodConfigApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load record.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  /** Binds a boolean field to the shared Toggle. */
  const ToggleField = ({ name, label, description }: { name: keyof FormValues; label: string; description?: string }) => (
    <Controller name={name} control={control} render={({ field }) => (
      <Toggle
        label={label}
        description={description}
        checked={field.value as boolean}
        onChange={field.onChange}
        disabled={fieldsDisabled}
      />
    )} />
  );

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={CreditCard} title="Payment Method Configuration"
        subtitle="Configure payment methods and their usage rules" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Payment Method"
        summary={record && (<>
            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{record.methodCode}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.methodName}</span>
            <Badge variant="info">{record.methodCategory}</Badge>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
        </>)}
      >
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Search size={13} />}
          loading={lookupBusy}
          onClick={() => setSearchOpen(true)}
          disabled={screen.isNew || screen.isEdit || saving}
        >
          Browse
        </Button>
        {screen.isInitial && canCreate && (
          <Button variant="outline" size="sm" leftIcon={<Plus size={13} />} onClick={handleNewRecord}>
            New
          </Button>
        )}
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Method Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Method Code *"
                    {...register('methodCode')}
                    error={errors.methodCode?.message}
                    disabled={fieldsDisabled || screen.isEdit}
                    placeholder="e.g. CASH"
                    className="uppercase font-mono"
                  />
                  <Input
                    label="Method Name *"
                    {...register('methodName')}
                    error={errors.methodName?.message}
                    disabled={fieldsDisabled}
                    placeholder="e.g. Cash Payment"
                  />
                  <div>
                    <Controller name="methodCategory" control={control} render={({ field }) => (
                      <Combobox
                        label="Category *"
                        options={METHOD_CATEGORIES.map(o => ({ value: o, label: o }))}
                        clearable={false}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={fieldsDisabled}
                      />
                    )} />
                    {errors.methodCategory && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.methodCategory.message}</p>}
                  </div>
                  <Input
                    label="Display Order"
                    type="number"
                    {...register('displayOrder')}
                    error={errors.displayOrder?.message}
                    disabled={fieldsDisabled}
                    placeholder="0"
                  />
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <ToggleField name="isActive" label="Is Active" />
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Textarea label="Remarks" {...register('remarks')} disabled={fieldsDisabled} rows={3} maxLength={500} placeholder="Optional notes…" />
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'Settings' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Behaviour Settings</h3>
                <div className="space-y-1">
                  <ToggleField name="requiresBankAccount"  label="Requires Bank Account"  description="A bank account must be linked to this payment." />
                  <ToggleField name="requiresChequeNumber" label="Requires Cheque Number"  description="A cheque number must be entered at transaction time." />
                  <ToggleField name="requiresChequeDate"   label="Requires Cheque Date"    description="A cheque with no date cannot be presented, and a post-dated cheque is a promise about one." />
                  <ToggleField name="requiresReference"    label="Requires Reference"      description="A reference field must be filled in." />
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4 mt-6">Allowed For</h3>
                <div className="space-y-1">
                  <ToggleField name="allowedForAr"        label="Accounts Receivable"    description="Can be used on customer receipt transactions." />
                  <ToggleField name="allowedForAp"        label="Accounts Payable"       description="Can be used on supplier payment transactions." />
                  <ToggleField name="allowedForPettyCash" label="Petty Cash"             description="Can be used in petty cash transactions." />
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<PaymentMethodConfigListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Payment Methods" columns={SEARCH_COLUMNS}
        fetchData={fetchItems} onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save New Payment Method' : 'Save Changes'}
        message={screen.isNew ? 'Save this new payment method?' : `Save changes to "${record?.methodName}"?`}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Payment Method"
        message={<span>Delete method <strong>{record?.methodCode} — {record?.methodName}</strong>?</span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};
